import {useCallback} from 'react';
import {QueryKey, useQueryClient, useMutation, useQuery} from 'react-query';
import {logError} from '@yandex-int/pythia-libs/lib/rum';

import {SurveySlug, RevisionContext, RevisionContextWrapper} from 'client/common/types';

import {
    getRevisionContext,
    updateRevisionContextTemplate,
    updateRevisionContextSettings
} from 'client/common/utils/api-client';

import {useSurveySlug} from 'client/common/contexts/slug';

import {getRevisionKey, useCurrentRevision, useCurrentRevisionId} from './index';

const REVISION_CONTEXT_KEY = 'revision_context';

export function getRevisionContextKey(slug: SurveySlug, revisionIndex: number): QueryKey {
    return [REVISION_CONTEXT_KEY, {slug, revisionIndex}];
}

export function useRevisionContext() {
    const slug = useSurveySlug();
    const {data: revision} = useCurrentRevision(slug);

    const revisionId = revision?.id ?? 0;

    return useQuery(
        getRevisionContextKey(slug, revisionId),
        async () => {
            const {data} = await getRevisionContext({slug, revisionIndex: revisionId});

            // Обертка в виде объекта, тк возникло странное поведение, что observer не узнает, если новое значение null
            // Воспроизвести поведение не удалось. Видимо, проблема где-то у нас, но непонятно где.
            // PYTHIADEV-5735

            return {
                value: data
            };
        },
        {
            enabled: revisionId > 0
        }
    );
}

export function useRevisionContextTemplate() {
    const {data: revisionContext} = useRevisionContext();

    return revisionContext?.value?.template ?? {};
}

export function useUpdateRevisionContextTemplate() {
    const slug = useSurveySlug();
    const revisionId = useCurrentRevisionId(slug);

    const key = getRevisionContextKey(slug, revisionId);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateRevisionContextTemplate, {
        onMutate: async ({template}) => {
            await queryClient.cancelQueries(key);

            queryClient.setQueryData(key, (old: RevisionContextWrapper) => ({
                value: {
                    ...old.value,
                    template
                }
            }));
        },
        onError: async () => {
            await queryClient.refetchQueries(key);
        }
    });

    return useCallback(
        async (template: any) => {
            await mutateAsync({
                slug,
                revisionId,
                template
            });
        },
        [mutateAsync, slug, revisionId]
    );
}

export function useSetRevisionContextTemplate() {
    const slug = useSurveySlug();
    const {data: revision} = useCurrentRevision(slug);

    const queryClient = useQueryClient();

    const revisionId = revision?.id || 0;

    if (!revisionId) {
        logError({message: 'Empty revisionId', err: new Error('No revision for change context')});
    }

    return (template: RevisionContext['template']) => {
        queryClient.setQueryData(
            getRevisionContextKey(slug, revisionId),
            (old: RevisionContextWrapper) => {
                return {
                    value: {
                        ...old.value,
                        template
                    }
                };
            }
        );
    };
}

export function useRevisionContextSettings(): RevisionContext['settings'] {
    const {data: revisionContext} = useRevisionContext();

    return revisionContext?.value?.settings ?? {};
}

export function useUpdateRevisionContextSettings() {
    const slug = useSurveySlug();
    const revisionId = useCurrentRevisionId(slug);

    const key = getRevisionContextKey(slug, revisionId);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(updateRevisionContextSettings, {
        onMutate: async ({settings}) => {
            await queryClient.cancelQueries(key);

            queryClient.setQueryData(key, (old: RevisionContextWrapper) => ({
                value: {
                    ...old.value,
                    settings
                }
            }));
        },
        onError: async () => {
            await queryClient.refetchQueries([key]);
        },
        onSettled: async () => {
            await queryClient.refetchQueries(getRevisionKey(slug, revisionId));
        }
    });

    return useCallback(
        async (settings: RevisionContext['settings']) => {
            await mutateAsync({
                slug,
                revisionId,
                settings
            });
        },
        [mutateAsync, slug, revisionId]
    );
}

/* function useSetRevisionContextSettings() {
    const slug = useSurveySlug();
    const {data: revision} = useCurrentRevision(slug);

    const revisionId = revision?.id || 0;

    if (!revisionId) {
        logError({err: new Error('No revision for change context')});
    }

    return (settings: RevisionContext['settings']) => {
        queryCache.setQueryData(getRevisionContextKey(slug, revisionId), (old: RevisionContext) => {
            return {
                ...old,
                settings
            };
        });
    };
}
 */
